import { format, subDays } from "date-fns";

// Invoice data structure
export interface InvoiceData {
  id: string;
  invoiceNumber: string;
  customerName: string;
  invoiceType: "membership" | "event_ticket" | "sponsorship";
  amount: number;
  status: "paid" | "pending" | "overdue";
  issueDate: string;
  dueDate: string;
  paidDate?: string;
  notes?: string;
}

// Invoice item structure
export interface InvoiceItem {
  description: string;
  quantity: number;
  unitPrice: number;
  total: number;
}

// Detailed invoice data with line items
export interface DetailedInvoiceData extends InvoiceData {
  items: InvoiceItem[];
  subtotal: number;
  tax: number;
  total: number;
  paymentMethod?: string;
  chamberInfo: {
    name: string;
    address: string;
    phone: string;
    email: string;
    website: string;
    logo?: string;
  };
  customerInfo: {
    name: string;
    company: string;
    address: string;
    email: string;
    phone: string;
  };
}

/**
 * Generates fake invoice data for testing and development
 * @param count Number of invoices to generate (defaults to 30)
 * @returns Array of InvoiceData objects
 */
export const generateFakeInvoiceData = (count: number = 30): InvoiceData[] => {
  const customerNames = [
    "ABC Company",
    "XYZ Corporation",
    "Local Business LLC",
    "Main Street Shop",
    "123 Industries",
    "Johnson & Associates",
    "Metro Services Inc",
    "Quality Consultants",
    "City Center Retail",
    "TechSolutions Group",
    "Northside Manufacturing",
    "Downtown Cafe",
    "Valley Home Services",
    "Professional Partners LLP",
    "Eastside Auto Parts",
    "Riverview Restaurant",
    "Golden Insurance Agency",
    "Swift Logistics Corp",
    "Urban Development Co",
    "Summit Investments",
  ];

  const invoiceTypes: InvoiceData["invoiceType"][] = [
    "membership",
    "event_ticket",
    "sponsorship",
  ];

  const statuses: InvoiceData["status"][] = ["paid", "pending", "overdue"];

  const generateDate = (daysAgo: number) => {
    return format(subDays(new Date(), daysAgo), "MMM dd, yyyy");
  };

  // Generate some amounts based on invoice type
  const getAmount = (type: InvoiceData["invoiceType"]) => {
    switch (type) {
      case "membership":
        return Math.floor(Math.random() * 1500) + 500; // $500-$2000
      case "event_ticket":
        return Math.floor(Math.random() * 400) + 100; // $100-$500
      case "sponsorship":
        return Math.floor(Math.random() * 4000) + 1000; // $1000-$5000
      default:
        return Math.floor(Math.random() * 1000) + 500; // $500-$1500
    }
  };

  return Array.from({ length: count }, (_, i) => {
    const invoiceType =
      invoiceTypes[Math.floor(Math.random() * invoiceTypes.length)];
    const status = statuses[Math.floor(Math.random() * statuses.length)];
    const amount = getAmount(invoiceType);

    let issueDate: string;
    let dueDate: string;
    let paidDate: string | undefined;

    if (status === "overdue") {
      // For overdue invoices, ensure due date is always in the past
      const daysOverdue = Math.floor(Math.random() * 90) + 1; // 1-90 days overdue
      const issueDaysAgo = daysOverdue + Math.floor(Math.random() * 30) + 30; // Issue date even further back

      issueDate = generateDate(issueDaysAgo);
      dueDate = generateDate(daysOverdue); // Due date is in the past
      paidDate = undefined; // Overdue invoices are not paid
    } else if (status === "paid") {
      // For paid invoices
      const issueDaysAgo = Math.floor(Math.random() * 60) + 1;
      const dueDaysAgo = Math.max(0, issueDaysAgo - 30); // Due 30 days after issue
      const paidDaysAgo = Math.floor(Math.random() * dueDaysAgo); // Paid sometime between due date and now

      issueDate = generateDate(issueDaysAgo);
      dueDate = generateDate(dueDaysAgo);
      paidDate = generateDate(paidDaysAgo);
    } else {
      // For pending invoices
      const issueDaysAgo = Math.floor(Math.random() * 30) + 1;
      const dueDaysFuture = Math.floor(Math.random() * 30) + 1; // Due in future

      issueDate = generateDate(issueDaysAgo);
      dueDate = format(
        new Date(Date.now() + dueDaysFuture * 24 * 60 * 60 * 1000),
        "MMM dd, yyyy"
      );
      paidDate = undefined;
    }

    return {
      id: `inv-${i + 1}`,
      invoiceNumber: `INV-${Math.floor(Math.random() * 10000)
        .toString()
        .padStart(4, "0")}`,
      customerName:
        customerNames[Math.floor(Math.random() * customerNames.length)],
      invoiceType,
      amount,
      status,
      issueDate,
      dueDate,
      paidDate,
      notes:
        Math.random() > 0.7 ? "Special terms and conditions apply" : undefined,
    };
  });
};

/**
 * Generates detailed invoice data for a single invoice
 * @param invoice Basic invoice data
 * @returns DetailedInvoiceData with line items and customer info
 */
export const generateDetailedInvoiceData = (
  invoice: InvoiceData
): DetailedInvoiceData => {
  // Generate line items based on invoice type
  let items: InvoiceItem[] = [];

  if (invoice.invoiceType === "membership") {
    items = [
      {
        description: "Annual Chamber Membership",
        quantity: 1,
        unitPrice: invoice.amount * 0.95, // 95% of total
        total: invoice.amount * 0.95,
      },
    ];

    // Sometimes add optional services
    if (Math.random() > 0.5) {
      items.push({
        description: "Enhanced Directory Listing",
        quantity: 1,
        unitPrice: invoice.amount * 0.05,
        total: invoice.amount * 0.05,
      });
    }
  } else if (invoice.invoiceType === "event_ticket") {
    const ticketPrice = Math.floor(
      invoice.amount / (Math.floor(Math.random() * 5) + 1)
    );
    const quantity = Math.max(1, Math.floor(invoice.amount / ticketPrice));

    items = [
      {
        description: "Business Forum Ticket",
        quantity,
        unitPrice: ticketPrice,
        total: ticketPrice * quantity,
      },
    ];
  } else if (invoice.invoiceType === "sponsorship") {
    items = [
      {
        description: "Event Sponsorship Package",
        quantity: 1,
        unitPrice: invoice.amount,
        total: invoice.amount,
      },
    ];
  }

  // Calculate totals
  const subtotal = items.reduce((sum, item) => sum + item.total, 0);
  const tax = 0; // No tax for chamber services
  const total = subtotal + tax;

  // Generate detailed customer info
  const [firstName, lastName] = [
    ["John", "Jane", "Michael", "Sarah", "David", "Lisa"][
      Math.floor(Math.random() * 6)
    ],
    ["Smith", "Johnson", "Brown", "Davis", "Wilson", "Taylor"][
      Math.floor(Math.random() * 6)
    ],
  ];

  return {
    ...invoice,
    items,
    subtotal,
    tax,
    total,
    paymentMethod:
      invoice.status === "paid"
        ? ["Credit Card", "Bank Transfer", "Cheque"][
            Math.floor(Math.random() * 3)
          ]
        : undefined,
    chamberInfo: {
      name: "Metro Chamber of Commerce",
      address: "123 Main Street, Suite 200, Metropolis, CA 90210",
      phone: "(555) 123-4567",
      email: "info@metrochamber.org",
      website: "www.metrochamber.org",
    },
    customerInfo: {
      name: `${firstName} ${lastName}`,
      company: invoice.customerName,
      address: "456 Business Ave, Metropolis, CA 90210",
      email: `${firstName.toLowerCase()}.${lastName.toLowerCase()}@${invoice.customerName
        .toLowerCase()
        .replace(/\s+/g, "")}.com`,
      phone: "(555) 987-6543",
    },
  };
};
